import { pxPerMeter, THICKNESS_INCH_OVERRIDE } from "config/constants";
import i18next from "i18next";
import numeral from "numeral";
import { useTranslation } from "react-i18next";

// Every unit conversion and every unit label of the app lives here (see US-IMPERIAL-TERV.md).
//
// The data model does not change: the plan is stored in meters, the tile and thickness sizes
// in cm, the canvas in px. Imperial is a display and input layer only, so nothing below the
// UI has to know about it.
//
// Rule: the unit string is always added by a formatter here, never by a translation key and
// never concatenated in JSX.

export type UnitSystem = "METRIC" | "IMPERIAL";

// the language that switches the app to Imperial
export const IMPERIAL_LANGUAGE = "en-US";

// The single source of the unit system - decision (A) of the plan lands here. It is tied to
// the language: switching to German inside the US build gives metric back. Moving to a
// build-bound model means changing this one function.
export const getUnitSystem = (lng?: string): UnitSystem => (lng?.toLowerCase() === IMPERIAL_LANGUAGE.toLowerCase() ? "IMPERIAL" : "METRIC");

export const useUnitSystem = (): UnitSystem => {
  const { i18n } = useTranslation();
  return getUnitSystem(i18n.language);
};

export const M_PER_FT = 0.3048;
export const M_PER_IN = 0.0254;
export const IN_PER_FT = 12;
export const CM_PER_IN = 2.54;
export const SQFT_PER_M2 = 10.7639104;
export const LB_PER_KG = 2.20462262;

// canvas unit size for 1 ft (px) - the Imperial counterpart of pxPerMeter
export const pxPerFoot = pxPerMeter * M_PER_FT;

export const m2ToSqFt = (m2: number): number => m2 * SQFT_PER_M2;
export const kgToLbs = (kg: number): number => kg * LB_PER_KG;
export const feetInchesToMeters = (ft: number, inch: number): number => (ft * IN_PER_FT + inch) * M_PER_IN;

export type FeetInchesOptions = {
  // the inch fraction to snap to. Everything the app shows uses whole inches (the default),
  // so the field and the summary never disagree on the same plan; a finer step is available.
  step?: number;
  // "floor" never overstates the value - that is what the size limits need
  mode?: "nearest" | "floor";
};

// A length in feet and inches. The inches are always inside [0, 12), so `ft` alone is never
// off by one after rounding up (11.7" with whole inch steps becomes 1' 0", not 0' 12").
export function metersToFeetInches(meters: number, options?: FeetInchesOptions): { ft: number; in: number } {
  const step = options?.step || 1;
  const sign = meters < 0 ? -1 : 1;
  const totalInches = Math.abs(meters) / M_PER_IN;
  const snapped = (options?.mode === "floor" ? Math.floor(totalInches / step) : Math.round(totalInches / step)) * step;

  const ft = Math.floor(snapped / IN_PER_FT);
  // the multiplication above can leave a float tail (0.30000000000000004), and the inches
  // are only ever shown with one decimal
  const inch = Math.round((snapped - ft * IN_PER_FT) * 1000) / 1000;

  return { ft: sign * ft, in: sign < 0 && ft === 0 ? -inch : inch };
}

// the format the rest of the app uses for numbers (the `common:number` interpolation)
const APP_NUMBER_FORMAT = "0.0,[00]";

function formatNumber(value: number, format = APP_NUMBER_FORMAT): string {
  try {
    // numeral only fails at format time for a locale it was never given (it lowercases the
    // key, so "en-US" finds the "en-us" registration in i18n.ts)
    numeral.locale(i18next.language || "en");
    return numeral(value).format(format);
  } catch {
    numeral.locale("en");
    return numeral(value).format(format);
  }
}

const formatInches = (inch: number): string => (Number.isInteger(inch) ? `${inch}` : `${Number(inch.toFixed(1))}`);

// 3.81 m -> 12' 6"
export function formatFeetInches(meters: number, options?: FeetInchesOptions): string {
  const { ft, in: inch } = metersToFeetInches(meters, options);
  const sign = ft < 0 || inch < 0 ? "-" : "";

  return `${sign}${Math.abs(ft)}' ${formatInches(Math.abs(inch))}"`;
}

const greatestCommonDivisor = (a: number, b: number): number => (b === 0 ? a : greatestCommonDivisor(b, a % b));

// 4.5 cm -> 1 3/4" (nearest 1/16 inch, reduced)
export function formatInchFraction(cm: number, denominator = 16): string {
  const inches = cm / CM_PER_IN;
  const whole = Math.floor(inches);
  let numerator = Math.round((inches - whole) * denominator);
  let den = denominator;

  if (numerator >= den) {
    return `${whole + 1}"`;
  }
  if (numerator === 0) {
    return `${whole}"`;
  }

  const divisor = greatestCommonDivisor(numerator, den);
  numerator /= divisor;
  den /= divisor;

  return whole > 0 ? `${whole} ${numerator}/${den}"` : `${numerator}/${den}"`;
}

// 12.5 m -> "12.50 m" | "41' 0""
export function formatLength(meters: number, unit: UnitSystem): string {
  return unit === "IMPERIAL" ? formatFeetInches(meters) : `${formatNumber(meters)} m`;
}

// The two sides of a plan, the way the summary lists them: 5 x 4 m -> 16' 5" × 13' 1".
// Metric keeps the "x" the summary has always used between the two numbers, Imperial gets the
// × of a pair, and the unit is written once, after both numbers.
export function formatPlanSize(widthMeters: number, heightMeters: number, unit: UnitSystem): string {
  if (unit !== "IMPERIAL") {
    return `${formatNumber(widthMeters)} x ${formatNumber(heightMeters)} m`;
  }
  return `${formatFeetInches(widthMeters)} × ${formatFeetInches(heightMeters)}`;
}

// 4.5 cm -> "4.50 cm" | "1 3/4""
export function formatThickness(cm: number, unit: UnitSystem): string {
  if (unit !== "IMPERIAL") {
    return `${formatNumber(cm)} cm`;
  }
  // an explicit value wins over the generic rounding, so a product can be communicated the
  // way the product page does it
  return THICKNESS_INCH_OVERRIDE[cm] || formatInchFraction(cm);
}

// The size limit of an input, for the "Max: ..." message. Metric keeps the bare number it
// has always shown, Imperial floors the feet and inches so the limit shown is never above
// the one actually validated: 50 m -> 164' 0", 30 m -> 98' 5".
export function formatSizeLimit(meters: number, unit: UnitSystem): string {
  return unit === "IMPERIAL" ? formatFeetInches(meters, { mode: "floor" }) : `${meters}`;
}

// 18.72 m2 -> "18.72 m2" | "201.5 sq ft"
// (a square foot is a tenth of a square meter, so the metric decimals would be noise here)
export function formatArea(m2: number, unit: UnitSystem): string {
  return unit === "IMPERIAL" ? `${formatNumber(m2ToSqFt(m2), "0,0.[0]")} sq ft` : `${formatNumber(m2)} m2`;
}

// 265 kg -> "265.00 kg" | "584 lbs"
export function formatMass(kg: number, unit: UnitSystem): string {
  return unit === "IMPERIAL" ? `${formatNumber(kgToLbs(kg), "0,0")} lbs` : `${formatNumber(kg)} kg`;
}

// the coordinate readout that follows the mouse on the canvas. The unit is written once,
// after the pair, because the two numbers belong together: 2.50 × 1.20 m | 8' 2" × 3' 11"
export function formatCoordinates(xMeters: number, yMeters: number, unit: UnitSystem): string {
  if (unit === "IMPERIAL") {
    return `${formatFeetInches(xMeters)} × ${formatFeetInches(yMeters)}`;
  }
  return `${formatNumber(xMeters)} × ${formatNumber(yMeters)} m`;
}

// the size of one tile, which arrives in cm: 100 x 100 cm -> 3' 3" × 3' 3"
export function formatTileSize(cmWidth: number, cmHeight: number, unit: UnitSystem): string {
  if (unit !== "IMPERIAL") {
    return `${cmWidth} x ${cmHeight} cm`;
  }
  return `${formatFeetInches(cmWidth / 100)} × ${formatFeetInches(cmHeight / 100)}`;
}
